import { ArrowPathIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import cleanDeep from "clean-deep";
import { merge, setWith } from "lodash";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { Actions as ADVISORACTIONS } from "../../../store/actions/advisorActions";
import AddressLookUp from "../../common/AddressLookUp";
import Spinner from "../../common/Spinner";
import Tooltip from "@/tooltip";
import StatsModal from "./StatsModal";

const senderOptions = [
	{ value: "Agent", label: "Agent" },
	{ value: "Bureau", label: "Bureau" },
];
const bureauOptions = [
	{ value: "Advisory", label: "Advisory" },
	{ value: "Enterprise", label: "Enterprise" },
];

const AdvisorSettings = ({
	isAdmin,
	disabled,
	updateAdvisorDetails,
	saving,
	settings,
	getAdvisorSettings,
	advisorId,
	jurisdictionOptions,
	jurisdiction,
}) => {
	useEffect(() => {
		if (advisorId) getAdvisorSettings(advisorId);
	}, [advisorId]);

	const [data, setData] = useState({});
	const updateField = (field, value) => {
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);

		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });

		setData({ ...newData });
	};

	useEffect(() => {
		setData({
			jurisdiction: settings?.jurisdiction,
			name: settings?.name,
			address: settings?.address,
			hmrc: settings?.hmrc,
			agent: settings?.agent,
			contactNumber: settings?.contactNumber,
			bureau: settings?.bureau || "Advisory",
			vatNumber: settings?.vatNumber,
			billingEmail: settings?.billingEmail,
			sender: settings?.sender || jurisdiction == "UK" ? "Agent" : undefined,
			agentReference: settings?.agentReference,
		});
	}, [settings?.id]);

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const dataToSubmit = cleanDeep({
			...data,
		});

		updateAdvisorDetails({ id: advisorId, detailsToUpdate: dataToSubmit });
	};

	return (
		<>
			<div className="content bg-white">
				<form
					onSubmit={onSubmit}
					style={{ paddingBottom: "8rem" }}
				>
					<div
						className="block-content"
						style={{ paddingTop: 0 }}
					>
						<div
							className="col-xs-12 form-section"
							style={{ marginTop: 0 }}
						>
							<div className="col-xs-7 form-container">
								<h4>Advisor Details</h4>
							</div>
							{settings?.stats && (
								<div className="col-xs-4 pull-right">
									<StatsModal
										stats={settings?.stats}
										latestStats={[...settings?.stats]?.pop()}
									/>
								</div>
							)}
							<div className="form-container col-xs-12">
								<div className={`col-xs-${isAdmin ? 9 : 12} pl-0`}>
									<div className="form-material form-container">
										<input
											id="name"
											name="name"
											className="form-control form-input"
											required="required"
											value={data?.name}
											aria-required="true"
											onChange={(e) => {
												updateField(e.target.name, e.target.value);
											}}
										/>
										<label htmlFor="name">Name</label>
									</div>
								</div>
								{isAdmin ? (
									<div className="col-xs-3 pl-0">
										<div className="form-material form-container">
											<label htmlFor="bureau">Bureau</label>
											<Select
												options={bureauOptions}
												value={bureauOptions.find((i) => i.value == data?.bureau)}
												onChange={(selected) => {
													updateField("bureau", selected?.value || null);
												}}
											/>
										</div>
									</div>
								) : null}
								<div className="col-xs-4 pl-0">
									<div className="form-material form-container">
										<input
											id="contactNumber"
											name="contactNumber"
											className="form-control form-input"
											autoComplete="off"
											value={data?.contactNumber}
											onChange={(e) => {
												updateField(e.target.name, e.target.value);
											}}
										/>
										<label htmlFor="contactNumber">Contact Number</label>
									</div>
								</div>
								<div className="col-xs-4 pl-0">
									<div className="form-material form-container">
										<input
											id="billingEmail"
											name="billingEmail"
											type="email"
											className="form-control form-input"
											autoComplete="off"
											value={data?.billingEmail}
											onChange={(e) => {
												updateField(e.target.name, e.target.value);
											}}
										/>
										<label htmlFor="billingEmail">Billing Email</label>
									</div>
								</div>
								<div className="col-xs-4 pl-0">
									<div className="form-material form-container">
										<input
											className="form-control form-input"
											id="vatNumber"
											name="vatNumber"
											value={data?.vatNumber}
											autoComplete="off"
											onChange={(e) => {
												updateField(e.target.name, e.target.value);
											}}
										/>
										<label htmlFor="vatNumber">VAT Number</label>
									</div>
								</div>
								{["UK"].includes(data?.jurisdiction) ? (
									<>
										<div className="col-xs-4 pl-0">
											<div className="form-material form-container">
												<input
													className="form-control form-input"
													id="agentReference"
													name="agentReference"
													value={data?.agentReference}
													autoComplete="off"
													onChange={(e) => {
														updateField(e.target.name, e.target.value);
													}}
												/>
												<label htmlFor="agentReference">
													<Tooltip content="This reference is used by HMRC to identify the agent/bureau to the Government Gateway.">
														Agent/Bureau Reference
													</Tooltip>
												</label>
											</div>
										</div>
										<div className="col-xs-4 pl-0">
											<div className="form-material form-container">
												<Select
													options={senderOptions}
													value={
														senderOptions.find((i) => i.value == data?.sender) || {
															value: "Agent",
															label: "Agent",
														}
													}
													id="sender"
													name="sender"
													onChange={(selected) => {
														updateField("sender", selected?.value || null);
													}}
												/>
												<label htmlFor="sender">
													<Tooltip content="This will be used when submitting FPS for a company managed under your advisory. HMRC requires a distinction between Agent or Bureau.">
														Sender Reference
													</Tooltip>
												</label>
											</div>
										</div>
									</>
								) : null}
								<div className="clearfix" />
							</div>
						</div>
						{["UK"].includes(data?.jurisdiction) ? (
							<div
								className="col-xs-12 form-section"
								style={{ marginTop: 10 }}
							>
								<div className="col-xs-12 form-container">
									<h4>HMRC Credentials</h4>
								</div>
								<div className="form-container col-xs-12">
									<div className="col-xs-6 pl-0">
										<div className="form-material form-container">
											<input
												id="hmrc[senderId]"
												name="hmrc[senderId]"
												className="form-control form-input"
												autoComplete="off"
												value={data?.hmrc?.senderId}
												onChange={(e) => {
													updateField(e.target.name, e.target.value);
												}}
											/>
											<label htmlFor="hmrc[senderId]">Government Gateway ID</label>
										</div>
									</div>
									<div className="col-xs-6 pl-0">
										{data?.hmrc?.setPassword ? (
											<div
												onClick={() => {
													updateField("hmrc[setPassword]", false);
												}}
											>
												<ArrowPathIcon
													style={{ height: "1.5em", float: "left", marginRight: 5 }}
												/>
												<div style={{ float: "left" }}>Update Password</div>
											</div>
										) : (
											<div className="form-material form-container">
												<input
													id="hmrc[password]"
													name="hmrc[password]"
													type="password"
													className="form-control form-input"
													autoComplete="off"
													value={data?.hmrc?.password}
													onChange={(e) => {
														updateField(e.target.name, e.target.value);
													}}
												/>
												<label htmlFor="hmrc[password]">Government Gateway Password</label>
												{data?.hmrc?.setPassword === false ? (
													<div
														style={{
															position: "absolute",
															top: 6,
															right: 0,
															cursor: "pointer",
														}}
														onClick={() => {
															updateField("hmrc[setPassword]", true);
															updateField("hmrc[password]", undefined);
														}}
													>
														<XCircleIcon style={{ height: "1.5em", float: "left" }} />
														<div style={{ float: "left" }}>Cancel</div>
													</div>
												) : null}
											</div>
										)}
									</div>
								</div>
							</div>
						) : null}
						<div
							className="col-xs-12 form-section"
							style={{ marginTop: 10, marginRight: 10 }}
						>
							<div className="col-xs-12">
								<h4>Advisor Address</h4>
							</div>
							<div className="col-xs-6">
								<AddressLookUp
									updateInput={(input) => {
										updateField("address", {
											...input,
											number: input?.houseNumber,
											houseNumber: undefined,
											postalCode: input?.postCode,
											postCode: undefined,
										});
									}}
									disabled={disabled}
								/>
							</div>
							<div className="clearfix" />
							{["UK"].includes(data?.jurisdiction) ? (
								<>
									<div className="col-xs-6">
										<div className="form-material form-container">
											<input
												className="form-control form-input"
												id="address[subBuilding]"
												name="address[subBuilding]"
												value={data?.address?.subBuilding}
												onChange={(e) => {
													updateField(e.target.name, e.target.value);
												}}
												autoComplete="off"
												disabled={disabled}
											/>
											<label htmlFor="address[subBuilding]">Sub Building</label>
										</div>
									</div>
								</>
							) : null}

							<div className="col-xs-6">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="address[number]"
										name="address[number]"
										value={data?.address?.number}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="address[number]">Building</label>
								</div>
							</div>

							<div className={`col-xs-${["UK"].includes(data?.jurisdiction) ? "12" : "6"}`}>
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="address[street]"
										name="address[street]"
										value={data?.address?.street}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="address[street]">Street</label>
								</div>
							</div>

							<div className="col-xs-6">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="address[city]"
										name="address[city]"
										value={data?.address?.city}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="address[city]">City</label>
								</div>
							</div>

							{["UK"].includes(data?.jurisdiction) ? (
								<>
									<div className="col-xs-6">
										<div className="form-material form-container">
											<input
												className="form-control form-input"
												id="address[locality]"
												name="address[locality]"
												value={data?.address?.locality}
												onChange={(e) => {
													updateField(e.target.name, e.target.value);
												}}
												autoComplete="off"
												disabled={disabled}
											/>
											<label htmlFor="address[locality]">Locality</label>
										</div>
									</div>
									<div className="col-xs-4">
										<div className="form-material form-container">
											<input
												className="form-control form-input"
												id="address[county]"
												name="address[county]"
												value={data?.address?.county}
												onChange={(e) => {
													updateField(e.target.name, e.target.value);
												}}
												autoComplete="off"
												disabled={disabled}
											/>
											<label htmlFor="address[county]">County</label>
										</div>
									</div>
									<div className="col-xs-4">
										<div className="form-material form-container">
											<input
												className="form-control form-input"
												id="address[country]"
												name="address[country]"
												value={data?.address?.country}
												onChange={(e) => {
													updateField(e.target.name, e.target.value);
												}}
												autoComplete="off"
												disabled={disabled}
											/>
											<label htmlFor="address[country]">Country</label>
										</div>
									</div>
								</>
							) : null}

							<div className={`col-xs-${["UK"].includes(data?.jurisdiction) ? 4 : 6}`}>
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="address[postalCode]"
										name="address[postalCode]"
										value={data?.address?.postalCode}
										autoComplete="off"
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										disabled={disabled}
									/>
									<label htmlFor="address[postalCode]">Postcode</label>
								</div>
							</div>
						</div>

						{jurisdiction == "UK" && (
							<>
								<div
									className="col-xs-12 form-section"
									style={{ marginTop: 0 }}
								>
									<div className="col-xs-12 form-container">
										<h4>Agent Details</h4>
									</div>
									<div className="form-container col-xs-12">
										<div className="col-xs-3 pl-0">
											<div className="form-material form-container">
												<input
													id="agent[title]"
													name="agent[title]"
													className="form-control form-input"
													autoComplete="off"
													value={data?.agent?.title}
													onChange={(e) => {
														updateField(e.target.name, e.target.value);
													}}
												/>
												<label htmlFor="agent[title]">Title</label>
											</div>
										</div>

										<div className="col-xs-3 pl-0">
											<div className="form-material form-container">
												<input
													id="agent[givenName]"
													name="agent[givenName]"
													className="form-control form-input"
													autoComplete="off"
													value={data?.agent?.givenName}
													onChange={(e) => {
														updateField(e.target.name, e.target.value);
													}}
												/>
												<label htmlFor="agent[givenName]">First Name</label>
											</div>
										</div>

										<div className="col-xs-3 pl-0">
											<div className="form-material form-container">
												<input
													id="agent[familyName]"
													name="agent[familyName]"
													className="form-control form-input"
													autoComplete="off"
													value={data?.agent?.familyName}
													onChange={(e) => {
														updateField(e.target.name, e.target.value);
													}}
												/>
												<label htmlFor="agent[familyName]">Last Name</label>
											</div>
										</div>

										<div className="col-xs-3 pl-0">
											<div className="form-material form-container">
												<input
													id="agent[email]"
													name="agent[email]"
													className="form-control form-input"
													autoComplete="off"
													type="email"
													value={data?.agent?.email}
													onChange={(e) => {
														updateField(e.target.name, e.target.value);
													}}
												/>
												<label htmlFor="agent[email]">Email</label>
											</div>
										</div>

										<div className="col-xs-3 pl-0">
											<div className="form-material form-container">
												<input
													id="agent[phone]"
													name="agent[phone]"
													className="form-control form-input"
													autoComplete="off"
													value={data?.agent?.phone}
													onChange={(e) => {
														updateField(e.target.name, e.target.value);
													}}
												/>
												<label htmlFor="agent[phone]">Phone Number</label>
											</div>
										</div>

										<div className="col-xs-3 pl-0">
											<div className="form-material form-container">
												<input
													id="agent[extension]"
													name="agent[extension]"
													className="form-control form-input"
													autoComplete="off"
													value={data?.agent?.extension}
													onChange={(e) => {
														updateField(e.target.name, e.target.value);
													}}
												/>
												<label htmlFor="agent[extension]">Extension</label>
											</div>
										</div>

										<div className="col-xs-3 pl-0">
											<div className="form-material form-container">
												<input
													id="agent[fax]"
													name="agent[fax]"
													className="form-control form-input"
													autoComplete="off"
													value={data?.agent?.fax}
													onChange={(e) => {
														updateField(e.target.name, e.target.value);
													}}
												/>
												<label htmlFor="agent[fax]">Fax</label>
											</div>
										</div>

										<div className="col-xs-3 pl-0">
											<div className="form-material form-container">
												<input
													id="agent[faxExtension]"
													name="agent[faxExtension]"
													className="form-control form-input"
													autoComplete="off"
													value={data?.agent?.faxExtension}
													onChange={(e) => {
														updateField(e.target.name, e.target.value);
													}}
												/>
												<label htmlFor="agent[faxExtension]">Fax Extension</label>
											</div>
										</div>
									</div>
								</div>
							</>
						)}
						<div
							className="col-xs-12 form-section"
							style={{ marginLeft: 0, marginTop: 10 }}
						>
							<div className="col-xs-12">
								<h4>Payroll Jurisdiction</h4>
							</div>
							<div className="col-xs-6 form-container">
								<div className="form-material">
									<Select
										options={jurisdictionOptions}
										value={jurisdictionOptions.find((i) => i.value == data?.jurisdiction)}
										name="jurisdiction"
										onChange={(selected) => {
											updateField("jurisdiction", selected?.value || null);
										}}
									/>
								</div>
							</div>
						</div>
					</div>
					<div className="col-xs-12">
						<button
							className="btn btn-success right"
							type="submit"
						>
							{saving ? (
								<Spinner
									style={{ height: "1.5em", verticalAlign: "middle", float: "left" }}
									className="animate-spin -ml-1 mr-3 h-5 w-5 "
								/>
							) : (
								<CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
							)}
							<div style={{ float: "left", paddingLeft: 5 }}>{saving ? "Saving..." : "Save"}</div>
						</button>
					</div>
				</form>
				<div className="clearfix" />
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	return {
		isAdmin: state.my.profile.isAdmin,
		saving: state.advisors.updatingDetails,
		settings: state.advisors.settings || {},
		advisorId: state.my.profile.advisorId,
		jurisdictionOptions: (state.jurisdictions || [])?.map(({ countryCode, name }) => ({
			label: `${name}/${countryCode}`,
			value: countryCode,
		})),
		jurisdiction: state.advisors?.settings?.jurisdiction,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getAdvisorSettings: (id) => dispatch(ADVISORACTIONS.getAdvisorSettings(id)),
	updateAdvisorDetails: ({ id, detailsToUpdate }) =>
		dispatch(ADVISORACTIONS.updateAdvisorDetails({ id, detailsToUpdate })),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdvisorSettings);
